<script setup lang="ts">
   import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue';
   import { XMarkIcon } from '@heroicons/vue/20/solid';

   import { clientAccountKey, isImpersonatingClientToken } from '~/utils/clientToken';

   const { t, locale } = useI18n();
   const route = useRoute();
   const { isClientAuthenticated, clientToken } = useAuthClientStorage();
   const { state: appPrompt, setBlocker } = useClientMobileAppPrompt();
   const { needToChangeTimeZone } = storeToRefs(useClientInfo());
   const { anonymousId, anonymousToken } = storeToRefs(useClientAuthStore());
   const prompt = useAdConsentPrompt();
   const { open, view, choice, saving, saveFailed, canDismiss } = prompt;
   const isRtl = computed(() => locale.value === 'ar');
   const isSettings = computed(() => view.value === 'settings');
   // Settings always has a way back; the first screen only after a failed save.
   const showClose = computed(() => isSettings.value || canDismiss.value);

   const panel = ref<HTMLElement | null>(null);
   const policyUrl = computed(() => cookiePolicyUrl(locale.value));

   // Anything else that owns the screen goes first: the welcome and app dialogs,
   // the teen emergency form, feedback and survey, the time zone prompt, or any
   // dialog a page opened itself (booking, reschedule, cancel). A visitor has none of
   // the signed-in prompts, and that state stays blocked while there is no profile.
   const isBlocked = () =>
      !isClientAuthenticated.value ?
         document.querySelector('[data-base-dialog]') !== null
      :  appPrompt.value.routeName !== String(route.name || '') ||
      appPrompt.value.blocked ||
      appPrompt.value.showAppPrompt ||
      appPrompt.value.appPromptPending ||
      appPrompt.value.showWelcome ||
      appPrompt.value.emergencyPending ||
      needToChangeTimeZone.value ||
      document.querySelector('[data-base-dialog]') !== null;

   watch(
      () => [
         route.name,
         route.query,
         isClientAuthenticated.value,
         clientToken.value?.access_token,
         anonymousId.value,
         anonymousToken.value,
         appPrompt.value,
         needToChangeTimeZone.value,
      ],
      () =>
         prompt.sync({
            routeName: String(route.name || ''),
            query: route.query,
            authenticated: isClientAuthenticated.value,
            impersonating: isImpersonatingClientToken(clientToken.value?.access_token),
            accountKey: clientAccountKey(clientToken.value?.access_token),
            // Both are needed: the journey routes refuse an id without its owner token.
            journeyKey:
               anonymousId.value && anonymousToken.value ? `journey:${anonymousId.value}` : null,
            isBlocked,
         }),
      { immediate: true, flush: 'post' },
   );

   // The welcome and app dialogs wait while this one is up.
   watch(open, (isOpen) => setBlocker('ad-consent', isOpen), { immediate: true });

   const isOpen = computed({
      get: () => open.value,
      set: (value: boolean) => {
         if (!value) prompt.dismiss();
      },
   });

   const focusable = () =>
      Array.from(
         panel.value?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]') ?? [],
      );

   const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
         prompt.close();
         return;
      }
      if (event.key !== 'Tab') return;
      const controls = focusable();
      if (!controls.length) {
         event.preventDefault();
         panel.value?.focus({ preventScroll: true });
         return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      const outside = !panel.value?.contains(active) || active === panel.value;
      if (event.shiftKey && (outside || active === first)) {
         event.preventDefault();
         last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
         event.preventDefault();
         first.focus();
      }
   };

   let returnFocusTo: HTMLElement | null = null;
   watch(
      open,
      async (isOpen) => {
         if (isOpen) {
            returnFocusTo = document.activeElement as HTMLElement | null;
            window.addEventListener('keydown', onKeydown);
            await nextTick();
            panel.value?.focus({ preventScroll: true });
         } else {
            window.removeEventListener('keydown', onKeydown);
            returnFocusTo?.focus?.({ preventScroll: true });
            returnFocusTo = null;
         }
      },
      { immediate: true },
   );
   onUnmounted(() => {
      window.removeEventListener('keydown', onKeydown);
      setBlocker('ad-consent', false);
   });

   const focusPrimary = async () => {
      await nextTick();
      const primary = panel.value?.querySelector<HTMLElement>('[data-ad-consent-primary]');
      (primary ?? focusable()[0] ?? panel.value)?.focus({ preventScroll: true });
   };

   // The buttons are disabled while a save is in flight, which drops focus; put
   // it back on the primary button of the view that is showing once they are
   // usable again, so retry is the default.
   watch(saving, (isSaving) => {
      if (isSaving || !open.value) return;
      void focusPrimary();
   });

   // A view change replaces the dialog's contents; focus goes to the panel so a
   // screen reader reads the new title, and Tab stays inside from there.
   watch(view, async () => {
      if (!open.value) return;
      await nextTick();
      panel.value?.focus({ preventScroll: true });
   });
</script>

<template>
   <BaseDialog v-model:open-dialog="isOpen" size="sm" preserve-log>
      <template #header></template>
      <div
         ref="panel"
         role="dialog"
         aria-modal="true"
         :aria-labelledby="isSettings ? 'ad-consent-settings-title' : 'ad-consent-prompt-title'"
         :aria-describedby="
            isSettings ? 'ad-consent-settings-description' : 'ad-consent-prompt-description'
         "
         tabindex="-1"
         class="flex flex-col gap-4 outline-none"
      >
         <div class="flex items-start justify-between gap-3">
            <h2
               v-if="isSettings"
               id="ad-consent-settings-title"
               class="text-xl font-medium"
            >
               {{ t('adConsent.promptSettingsTitle') }}
            </h2>
            <h2 v-else id="ad-consent-prompt-title" class="text-xl font-medium">
               {{ t('adConsent.promptTitle') }}
            </h2>
            <button
               v-if="showClose"
               type="button"
               class="shrink-0 rounded text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
               :aria-label="t('accountDetail.close')"
               @click="prompt.close()"
            >
               <XMarkIcon class="h-6 w-6" aria-hidden="true" />
            </button>
         </div>

         <template v-if="!isSettings">
            <p
               id="ad-consent-prompt-description"
               class="text-sm leading-5 font-normal text-gray-300"
            >
               {{ t('adConsent.promptBody') }}
            </p>
            <div v-if="saveFailed" role="alert" class="text-xs text-error-500">
               {{ t('adConsent.saveError') }}
            </div>
            <div class="flex flex-wrap items-center justify-between gap-3 pt-1">
               <BaseButton
                  type="button"
                  variant="plain"
                  color="primary-500"
                  class="font-medium"
                  :disabled="saving"
                  @click="prompt.manage()"
               >
                  {{ t('adConsent.promptManage') }}
               </BaseButton>
               <BaseButton
                  type="button"
                  color="primary-500"
                  data-ad-consent-primary
                  :disabled="saving"
                  @click="prompt.answer(true)"
               >
                  {{ t('adConsent.promptAllow') }}
               </BaseButton>
            </div>
         </template>

         <template v-else>
            <div class="flex flex-col gap-1">
               <div class="flex items-center justify-between gap-3">
                  <span class="text-sm font-medium">{{ t('adConsent.promptNecessaryTitle') }}</span>
                  <span class="shrink-0 text-xs font-medium text-primary-600">
                     {{ t('adConsent.promptAlwaysOn') }}
                  </span>
               </div>
               <span class="text-sm leading-5 font-normal text-gray-300">
                  {{ t('adConsent.promptNecessaryBody') }}
               </span>
            </div>
            <SwitchGroup as="div" class="flex items-start gap-3">
               <Switch
                  v-model="choice"
                  :disabled="saving"
                  :class="[
                     choice ? 'bg-primary-600' : 'bg-gray-200',
                     saving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                     'relative mt-0.5 inline-flex h-5 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
                  ]"
               >
                  <span
                     aria-hidden="true"
                     :class="[
                        choice ?
                           isRtl ? '-translate-x-5'
                           :  'translate-x-5'
                        :  'translate-x-0',
                        'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                     ]"
                  />
               </Switch>
               <div class="flex flex-col gap-1">
                  <SwitchLabel as="span" class="text-sm font-medium">
                     {{ t('adConsent.promptAdvertisingLabel') }}
                  </SwitchLabel>
                  <SwitchDescription
                     id="ad-consent-settings-description"
                     as="span"
                     class="text-sm leading-5 font-normal text-gray-300"
                  >
                     {{ t('adConsent.description') }}
                  </SwitchDescription>
               </div>
            </SwitchGroup>
            <p class="text-sm leading-5 font-normal text-gray-300">
               {{ t('adConsent.promptScope') }}
            </p>
            <a
               :href="policyUrl"
               target="_blank"
               rel="noopener noreferrer"
               class="w-fit text-sm text-primary-600 underline"
            >
               {{ t('adConsent.policyLink') }}
            </a>
            <div v-if="saveFailed" role="alert" class="text-xs text-error-500">
               {{ t('adConsent.saveError') }}
            </div>
            <div class="flex flex-wrap items-center justify-end gap-3 pt-1">
               <BaseButton
                  type="button"
                  color="primary-500"
                  data-ad-consent-primary
                  :disabled="saving"
                  @click="prompt.confirm()"
               >
                  {{ t('adConsent.promptConfirm') }}
               </BaseButton>
            </div>
         </template>
      </div>
   </BaseDialog>
</template>
